import { useEffect, useState } from "react";
import { useDimensions, useLanguage } from "../../../hooks";

import {
  BoardBody,
  BoardDotStatus,
  BoardHead,
  BoardSortedDirectionProps,
  BoardSortedDirectionType,
  BoardTd,
  BoardTdMobileItem,
  BoardTh,
  BoardTr,
  getSortedBoard,
  IconButton,
} from "../../../components";
import { CheckmarkCircleOutline, EyeOutline } from "../../../assets";
import { SupportServices } from "../../../services";
import {
  AdminIncidentProps,
  getIncidentStatusColors,
  SupportIncidentsProps,
} from "../support.props";
import { filterData } from "../../../utils";

const safeIncludes = (value: unknown, search: string): boolean =>
  String(value ?? "")
    .toLowerCase()
    .includes(search.toLowerCase());

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  return isNaN(date.getTime()) ? iso : date.toLocaleString("es-ES");
};

const matchesSearch = (card: AdminIncidentProps, search: string): boolean =>
  safeIncludes(card.reference, search) ||
  safeIncludes(card.date, search) ||
  safeIncludes(card.description, search) ||
  safeIncludes(card.name, search) ||
  safeIncludes(card.status, search) ||
  safeIncludes(card.type, search) ||
  safeIncludes(card.requesterEmail, search);

const SupportIncidents: React.FC<SupportIncidentsProps> = ({
  search = "",
  filters,
  setExportDisabled,
  isAdmin,
  onOpenDetail,
}) => {
  const { t } = useLanguage();
  const { width } = useDimensions();

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [data, setData] = useState<AdminIncidentProps[]>([]);
  const [base, setBase] = useState<AdminIncidentProps[]>([]);
  const [cards, setCards] = useState<AdminIncidentProps[]>([]);
  const [sortConfig, setSortConfig] = useState<BoardSortedDirectionProps>({
    key: "",
    direction: "",
  });
  const [activeId, setActiveId] = useState<string | null>(null);

  const handleToggle = (id: string) => {
    setActiveId((prev) => (prev === id ? null : id));
  };

  const sortData = (key: any) => {
    let direction: BoardSortedDirectionType = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sorted = getSortedBoard(cards, key, direction);

    setSortConfig({ key, direction });
    setCards(sorted);

    if (sortConfig.key === key && sortConfig.direction === "desc") {
      setSortConfig({ key: "", direction: "" });
      const searchedCards =
        search.length > 0 ? base.filter((c) => matchesSearch(c, search)) : base;
      setCards(searchedCards);
    }
  };

  useEffect(() => setIsMobile(width <= 650), [width]);
  useEffect(() => setExportDisabled(isMobile), [isMobile]);

  useEffect(() => {
    // Ejecutar función de búsqueda según cada cambio en el search
    const searchedCards =
      search.length > 0 ? base.filter((c) => matchesSearch(c, search)) : base;
    setCards(searchedCards);
  }, [search, base]);

  useEffect(() => {
    const filteredCards = filterData(data, filters);
    setBase(filteredCards);
  }, [filters]);

  useEffect(() => {
    // Evita que una respuesta obsoleta (p.ej. la del primer render con isAdmin aún
    // sin resolver) sobrescriba el estado con datos de una petición más reciente.
    let cancelled = false;

    (async () => {
      const { getIncidents, getAllIncidents } = SupportServices();
      const result = isAdmin ? await getAllIncidents() : await getIncidents();
      if (cancelled) return;

      const incidents = (result?.data ?? []) as AdminIncidentProps[];
      setData(incidents);
      setBase(incidents);
      setCards(incidents);
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  return (
    <>
      <BoardHead>
        <BoardTr>
          <BoardTh
            onClick={() => sortData("reference")}
            text={t.supportScreen.table.thead.reference}
          />
          {!isMobile && (
            <BoardTh
              onClick={() => sortData("type")}
              text={t.supportScreen.table.thead.type}
            />
          )}
          <BoardTh
            onClick={() => sortData("name")}
            text={t.supportScreen.table.thead.name}
          />
          {!isMobile && (
            <>
              {isAdmin && (
                <BoardTh
                  onClick={() => sortData("requesterEmail")}
                  text={t.supportScreen.table.thead.requester}
                />
              )}
              <BoardTh
                onClick={() => sortData("description")}
                text={t.supportScreen.table.thead.description}
              />
              <BoardTh
                onClick={() => sortData("status")}
                text={t.supportScreen.table.thead.status}
              />
              <BoardTh
                onClick={() => sortData("date")}
                text={t.supportScreen.table.thead.date}
              />
              <BoardTh
                onClick={() => sortData("lastUpdate")}
                text={t.supportScreen.table.thead.lastUpdate}
              />
              <BoardTh text={t.supportScreen.table.thead.actions} />
            </>
          )}
        </BoardTr>
      </BoardHead>

      <BoardBody isMobile={isMobile}>
        {cards.map((card, index) => {
          const id: string = `${card.status}-${index}`;
          const isActive: boolean = activeId === id;
          const { primary: primaryColor, secondary: secondaryColor } =
            getIncidentStatusColors(card.status);
          // Identificador del ticket, con un punto si tiene mensajes no leídos.
          const reference = (
            <p
              className={`support-reference ${card.unread ? "unread" : ""}`}
              title={card.unread ? t.supportScreen.table.unread : undefined}
            >
              {card.unread && <span className="support-unread-dot" />}
              {card.reference}
            </p>
          );

          return (
            <BoardTr key={`support-card-${index}`}>
              {isMobile ? (
                <>
                  <BoardTd hasClassNameDirection={false}>
                    {reference}
                  </BoardTd>
                  <BoardTd
                    hasClassNameW100
                    hasClassNameDirection={false}
                    mobile={{
                      isMobile: true,
                      isActive: isActive,
                      onClick: () => handleToggle(id),
                      text: card.name,
                    }}
                  >
                    <BoardTdMobileItem title={t.supportScreen.table.thead.type}>
                      <p>{t.supportScreen.table.types[card.type]}</p>
                    </BoardTdMobileItem>
                    {isAdmin && (
                      <BoardTdMobileItem
                        title={t.supportScreen.table.thead.requester}
                      >
                        <p>{card.requesterEmail}</p>
                      </BoardTdMobileItem>
                    )}
                    <BoardTdMobileItem
                      title={t.supportScreen.table.thead.description}
                    >
                      <p>{card.description}</p>
                    </BoardTdMobileItem>
                    <BoardTdMobileItem
                      title={t.supportScreen.table.thead.status}
                    >
                      <BoardDotStatus
                        pColor={primaryColor}
                        sColor={secondaryColor}
                        text={t.supportScreen.table.tbody[card.status]}
                      />
                    </BoardTdMobileItem>
                    <BoardTdMobileItem title={t.supportScreen.table.thead.date}>
                      <p>{formatDateTime(card.date)}</p>
                    </BoardTdMobileItem>
                    <BoardTdMobileItem
                      title={t.supportScreen.table.thead.lastUpdate}
                    >
                      <p>{formatDateTime(card.lastUpdate)}</p>
                    </BoardTdMobileItem>
                    <BoardTdMobileItem
                      title={t.supportScreen.table.thead.actions}
                    >
                      <IconButton
                        icon={isAdmin ? CheckmarkCircleOutline : EyeOutline}
                        alt={
                          isAdmin
                            ? t.supportScreen.respond.button
                            : t.supportScreen.respond.view
                        }
                        onClick={() => onOpenDetail?.(card)}
                      />
                    </BoardTdMobileItem>
                  </BoardTd>
                </>
              ) : (
                <>
                  <BoardTd hasClassNameDirection={false}>
                    {reference}
                  </BoardTd>
                  <BoardTd>
                    <p>{t.supportScreen.table.types[card.type]}</p>
                  </BoardTd>
                  <BoardTd>
                    <p className="board-bold">{card.name}</p>
                  </BoardTd>
                  {isAdmin && (
                    <BoardTd>
                      <p>{card.requesterEmail}</p>
                    </BoardTd>
                  )}
                  <BoardTd>
                    <p>{card.description}</p>
                  </BoardTd>
                  <BoardTd hasClassNameDirection={false}>
                    <BoardDotStatus
                      pColor={primaryColor}
                      sColor={secondaryColor}
                      text={t.supportScreen.table.tbody[card.status]}
                    />
                  </BoardTd>
                  <BoardTd>
                    <p>{formatDateTime(card.date)}</p>
                  </BoardTd>
                  <BoardTd>
                    <p>{formatDateTime(card.lastUpdate)}</p>
                  </BoardTd>
                  <BoardTd hasClassNameDirection={false}>
                    <IconButton
                      icon={isAdmin ? CheckmarkCircleOutline : EyeOutline}
                      alt={
                        isAdmin
                          ? t.supportScreen.respond.button
                          : t.supportScreen.respond.view
                      }
                      onClick={() => onOpenDetail?.(card)}
                    />
                  </BoardTd>
                </>
              )}
            </BoardTr>
          );
        })}
      </BoardBody>
    </>
  );
};

export default SupportIncidents;
